<template>
  <section class="mx-auto max-w-6xl px-6 py-10">
    <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div>
        <p class="text-xs uppercase tracking-[0.4em] text-steel-400">{{ $t('adminPage.eyebrow') }}</p>
        <h1 class="mt-2 font-display text-3xl font-semibold text-white">{{ $t('adminPage.title') }}</h1>
        <p class="mt-2 max-w-2xl text-sm text-steel-300">
          {{ $t('adminPage.description') }}
        </p>
      </div>
      <div class="flex flex-wrap gap-2">
        <button
          type="button"
          class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
          :disabled="loading"
          @click="refreshUsers"
        >
          {{ loading ? $t('adminPage.refreshing') : $t('adminPage.refresh') }}
        </button>
      </div>
    </div>

    <div class="mt-8 grid gap-4 sm:grid-cols-3">
      <div class="rounded-3xl border border-white/10 bg-white/5 p-5">
        <p class="text-xs uppercase tracking-[0.3em] text-steel-400">{{ $t('adminPage.totalMembers') }}</p>
        <div v-if="loading && !stats.total && !stats.admins && !stats.banned" class="mt-3 h-8 w-20 rounded bg-white/10 animate-pulse"></div>
        <p v-else class="mt-3 text-2xl font-semibold text-white">{{ stats.total }}</p>
      </div>
      <div class="rounded-3xl border border-white/10 bg-white/5 p-5">
        <p class="text-xs uppercase tracking-[0.3em] text-steel-400">{{ $t('adminPage.admins') }}</p>
        <div v-if="loading && !stats.total && !stats.admins && !stats.banned" class="mt-3 h-8 w-20 rounded bg-white/10 animate-pulse"></div>
        <p v-else class="mt-3 text-2xl font-semibold text-white">{{ stats.admins }}</p>
      </div>
      <div class="rounded-3xl border border-white/10 bg-white/5 p-5">
        <p class="text-xs uppercase tracking-[0.3em] text-steel-400">{{ $t('adminPage.banned') }}</p>
        <div v-if="loading && !stats.total && !stats.admins && !stats.banned" class="mt-3 h-8 w-20 rounded bg-white/10 animate-pulse"></div>
        <p v-else class="mt-3 text-2xl font-semibold text-white">{{ stats.banned }}</p>
      </div>
    </div>

    <div class="mt-8 rounded-3xl border border-white/10 bg-midnight-900/60 p-5">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p class="text-xs uppercase tracking-[0.4em] text-steel-400">{{ $t('adminPage.broadcastEyebrow') }}</p>
          <h2 class="mt-2 font-display text-2xl font-semibold text-white">{{ $t('adminPage.broadcastTitle') }}</h2>
          <p class="mt-2 max-w-2xl text-sm text-steel-300">{{ $t('adminPage.broadcastDescription') }}</p>
        </div>
        <div class="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-xs text-steel-300">
          {{ $t('adminPage.broadcastAudience') }}
          <p class="mt-1 text-sm font-semibold text-white">{{ pushSubscriberCount }}</p>
        </div>
      </div>

      <div class="mt-6 grid gap-4 sm:grid-cols-2">
        <label class="block text-sm text-steel-200 sm:col-span-2">
          {{ $t('adminPage.broadcastTitleLabel') }}
          <input
            v-model.trim="broadcastForm.title"
            type="text"
            maxlength="80"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-500 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
          />
        </label>

        <label class="block text-sm text-steel-200 sm:col-span-2">
          {{ $t('adminPage.broadcastBodyLabel') }}
          <textarea
            v-model.trim="broadcastForm.body"
            rows="4"
            maxlength="240"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-500 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
          ></textarea>
        </label>

        <label class="block text-sm text-steel-200">
          {{ $t('adminPage.broadcastUrlLabel') }}
          <input
            v-model.trim="broadcastForm.url"
            type="text"
            placeholder="/notifications"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-500 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
          />
        </label>
      </div>

      <div class="mt-6 flex flex-wrap gap-2">
        <button
          type="button"
          class="rounded-2xl bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-midnight-900 transition hover:bg-neon-400 disabled:cursor-not-allowed disabled:opacity-70"
          :disabled="broadcastSending"
          @click="sendBroadcastPush"
        >
          {{ broadcastSending ? $t('adminPage.broadcastSending') : $t('adminPage.broadcastSend') }}
        </button>
        <button
          type="button"
          class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
          :disabled="pushSubscriberLoading"
          @click="fetchPushSubscriberCount"
        >
          {{ pushSubscriberLoading ? $t('adminPage.refreshing') : $t('adminPage.broadcastRefreshAudience') }}
        </button>
      </div>

      <div class="mt-8 grid gap-4 xl:grid-cols-2">
        <div class="rounded-2xl border border-emerald-400/20 bg-emerald-500/5 p-4">
          <div class="flex items-start justify-between gap-4">
            <div>
              <p class="text-xs uppercase tracking-[0.35em] text-emerald-200/80">{{ $t('adminPage.pushEnabledTitle') }}</p>
              <p class="mt-2 text-sm text-steel-300">{{ $t('adminPage.pushEnabledDescription') }}</p>
            </div>
            <span class="rounded-full border border-emerald-400/30 bg-emerald-400/10 px-3 py-1 text-xs font-semibold text-emerald-100">
              {{ subscribedMembers.length }}
            </span>
          </div>
          <div class="mt-4 max-h-72 space-y-3 overflow-y-auto pr-1">
            <div
              v-for="member in subscribedMembers"
              :key="`push-on-${member.id}`"
              class="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-3 py-3"
            >
              <router-link :to="`/u/${member.handle}`" class="min-w-0 flex items-center gap-3 hover:opacity-80 transition">
                <div class="h-10 w-10 overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/80">
                  <img
                    v-if="member.avatar"
                    :src="member.avatar"
                    alt=""
                    class="h-full w-full object-cover"
                    referrerpolicy="no-referrer"
                    crossorigin="anonymous"
                  />
                  <div v-else class="flex h-full w-full items-center justify-center text-xs font-semibold text-steel-200">
                    {{ member.initials }}
                  </div>
                </div>
                <div class="min-w-0">
                  <p class="truncate text-sm font-semibold text-white">{{ member.fullName || 'DRVN Member' }}</p>
                  <p class="truncate text-xs text-steel-400">@{{ member.handle }}</p>
                </div>
              </router-link>
              <div class="text-right">
                <p class="text-xs font-semibold text-emerald-100">{{ member.subscriptionCount }} {{ $t('adminPage.pushDevices') }}</p>
                <p class="text-[11px] text-steel-400">{{ formatAdminDate(member.lastSeenAt) }}</p>
              </div>
            </div>
            <p v-if="!subscribedMembers.length && !pushSubscriberLoading" class="text-sm text-steel-400">
              {{ $t('adminPage.pushEnabledEmpty') }}
            </p>
          </div>
        </div>

        <div class="rounded-2xl border border-amber-400/20 bg-amber-500/5 p-4">
          <div class="flex items-start justify-between gap-4">
            <div>
              <p class="text-xs uppercase tracking-[0.35em] text-amber-200/80">{{ $t('adminPage.pushDisabledTitle') }}</p>
              <p class="mt-2 text-sm text-steel-300">{{ $t('adminPage.pushDisabledDescription') }}</p>
            </div>
            <span class="rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-xs font-semibold text-amber-100">
              {{ unsubscribedMembers.length }}
            </span>
          </div>
          <div class="mt-4 max-h-72 space-y-3 overflow-y-auto pr-1">
            <div
              v-for="member in unsubscribedMembers"
              :key="`push-off-${member.id}`"
              class="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-white/5 px-3 py-3"
            >
              <router-link :to="`/u/${member.handle}`" class="min-w-0 flex items-center gap-3 hover:opacity-80 transition">
                <div class="h-10 w-10 overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/80">
                  <img
                    v-if="member.avatar"
                    :src="member.avatar"
                    alt=""
                    class="h-full w-full object-cover"
                    referrerpolicy="no-referrer"
                    crossorigin="anonymous"
                  />
                  <div v-else class="flex h-full w-full items-center justify-center text-xs font-semibold text-steel-200">
                    {{ member.initials }}
                  </div>
                </div>
                <div class="min-w-0">
                  <p class="truncate text-sm font-semibold text-white">{{ member.fullName || 'DRVN Member' }}</p>
                  <p class="truncate text-xs text-steel-400">@{{ member.handle }}</p>
                </div>
              </router-link>
              <div class="text-right">
                <p class="text-xs font-semibold text-amber-100">{{ $t('adminPage.pushNeedsHelp') }}</p>
                <p class="text-[11px] text-steel-400">#{{ member.memberNumber || '—' }}</p>
              </div>
            </div>
            <p v-if="!unsubscribedMembers.length && !pushSubscriberLoading" class="text-sm text-steel-400">
              {{ $t('adminPage.pushDisabledEmpty') }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-12">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p class="text-xs uppercase tracking-[0.4em] text-steel-400">{{ $t('adminPage.reportsEyebrow') }}</p>
          <h2 class="mt-2 font-display text-2xl font-semibold text-white">{{ $t('adminPage.queueTitle') }}</h2>
          <p class="mt-2 text-sm text-steel-300">
            {{ $t('adminPage.queueDescription') }}
          </p>
        </div>
        <button
          type="button"
          class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
          :disabled="reportsLoading"
          @click="fetchReports"
        >
          {{ reportsLoading ? $t('adminPage.refreshing') : $t('adminPage.refreshReports') }}
        </button>
      </div>

      <p v-if="reportsError" class="mt-6 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
        {{ reportsError }}
      </p>

      <div class="mt-6 rounded-3xl border border-white/10 bg-midnight-900/60">
        <div v-if="reportsLoading" class="px-6 py-6 space-y-4">
          <div v-for="item in 3" :key="`report-skeleton-${item}`" class="animate-pulse space-y-3">
            <div class="h-3 w-36 rounded bg-white/10"></div>
            <div class="h-4 w-5/6 rounded bg-white/10"></div>
            <div class="h-3 w-32 rounded bg-white/10"></div>
          </div>
        </div>
        <div v-else-if="!reports.length" class="px-6 py-6 text-sm text-steel-400">
          {{ $t('adminPage.noReports') }}
        </div>
        <div v-else class="divide-y divide-white/5">
          <div v-for="report in reports" :key="report.id" class="px-6 py-5">
            <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div class="flex flex-1 flex-col gap-3">
                <div class="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.3em] text-steel-400">
                  <span>{{ report.createdLabel }}</span>
                  <span class="rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em]"
                    :class="report.status === 'resolved' ? 'bg-emerald-500/20 text-emerald-200' : 'bg-amber-500/20 text-amber-200'">
                    {{ report.status || $t('adminPage.pending') }}
                  </span>
                </div>
                <p class="text-sm text-white">{{ report.reason }}</p>
                <div class="flex flex-wrap items-center gap-4 text-xs text-steel-300">
                  <router-link
                    v-if="report.postId"
                    :to="`/post/${report.postId}`"
                    class="text-neon-400 hover:text-neon-300"
                  >
                    {{ $t('adminPage.viewPost') }}
                  </router-link>
                  <router-link
                    v-if="report.reporterHandle"
                    :to="`/u/${report.reporterHandle}`"
                    class="text-steel-300 hover:text-white"
                  >
                    {{ $t('adminPage.reporter') }}: @{{ report.reporterHandle }}
                  </router-link>
                </div>
              </div>
              <div class="flex flex-wrap gap-2">
                <button
                  type="button"
                  class="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
                  @click="resolveReport(report)"
                >
                  {{ $t('adminPage.markResolved') }}
                </button>
                <button
                  v-if="report.postId"
                  type="button"
                  class="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-rose-100 transition hover:bg-rose-500/20"
                  @click="openDeleteReportConfirm(report)"
                >
                  {{ $t('adminPage.deletePost') }}
                </button>
                <button
                  type="button"
                  class="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-steel-200 transition hover:bg-white/10"
                  @click="deleteReportRecord(report)"
                >
                  {{ $t('adminPage.deleteReport') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-12">
      <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p class="text-xs uppercase tracking-[0.4em] text-steel-400">{{ $t('adminPage.siteIssues') }}</p>
          <h2 class="mt-2 font-display text-2xl font-semibold text-white">{{ $t('adminPage.userReports') }}</h2>
          <p class="mt-2 text-sm text-steel-300">
            {{ $t('adminPage.issuesDescription') }}
          </p>
        </div>
        <button
          type="button"
          class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
          :disabled="siteReportsLoading"
          @click="fetchSiteReports"
        >
          {{ siteReportsLoading ? $t('adminPage.refreshing') : $t('adminPage.refreshIssues') }}
        </button>
      </div>

      <p v-if="siteReportsError" class="mt-6 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
        {{ siteReportsError }}
      </p>

      <div class="mt-6 rounded-3xl border border-white/10 bg-midnight-900/60">
        <div v-if="siteReportsLoading" class="px-6 py-6 space-y-4">
          <div v-for="item in 3" :key="`site-report-skeleton-${item}`" class="animate-pulse space-y-3">
            <div class="h-3 w-32 rounded bg-white/10"></div>
            <div class="h-4 w-4/5 rounded bg-white/10"></div>
            <div class="h-3 w-24 rounded bg-white/10"></div>
          </div>
        </div>
        <div v-else-if="!siteReports.length" class="px-6 py-6 text-sm text-steel-400">
          {{ $t('adminPage.noIssues') }}
        </div>
        <div v-else class="divide-y divide-white/5">
          <div v-for="report in siteReports" :key="report.id" class="px-6 py-5">
            <div class="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div class="flex flex-1 flex-col gap-3">
                <div class="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.3em] text-steel-400">
                  <span>{{ report.createdLabel }}</span>
                  <span class="rounded-full px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em]"
                    :class="report.status === 'resolved' ? 'bg-emerald-500/20 text-emerald-200' : 'bg-amber-500/20 text-amber-200'">
                    {{ report.status || $t('adminPage.open') }}
                  </span>
                  <span class="rounded-full bg-white/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-white">
                    {{ report.category }}
                  </span>
                </div>
                <p class="text-sm text-white">{{ report.description }}</p>
                <div class="flex flex-wrap items-center gap-4 text-xs text-steel-300">
                  <span v-if="report.reporterHandle">{{ $t('adminPage.reporter') }}: @{{ report.reporterHandle }}</span>
                  <a
                    v-if="report.pageUrl"
                    :href="report.pageUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-neon-400 hover:text-neon-300"
                  >
                    {{ $t('adminPage.viewPage') }}
                  </a>
                </div>
              </div>
              <div class="flex flex-wrap gap-2">
                <button
                  type="button"
                  class="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
                  @click="resolveSiteReport(report)"
                >
                  {{ $t('adminPage.markResolved') }}
                </button>
                <button
                  type="button"
                  class="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-steel-200 transition hover:bg-white/10"
                  @click="deleteSiteReport(report)"
                >
                  {{ $t('adminPage.deleteIssue') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showDeleteReportConfirm"
      class="fixed inset-0 z-[140] flex items-center justify-center bg-black/70 px-6"
      @click.self="closeDeleteReportConfirm"
    >
      <div class="w-full max-w-md rounded-3xl border border-white/10 bg-midnight-900/95 p-6 shadow-glow">
        <h3 class="font-display text-xl font-semibold text-white">Delete reported post?</h3>
        <p class="mt-3 text-sm text-steel-300">
          This will permanently remove the post. This action cannot be undone.
        </p>
        <div class="mt-6 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
            @click="closeDeleteReportConfirm"
          >
            Cancel
          </button>
          <button
            type="button"
            class="rounded-2xl bg-rose-500 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-rose-400"
            :disabled="reportsLoading"
            @click="confirmDeleteReportedPost"
          >
            Delete
          </button>
        </div>
      </div>
    </div>

    <div class="mt-8 flex flex-col gap-4 rounded-3xl border border-white/10 bg-midnight-900/60 p-5 sm:flex-row sm:items-center">
      <div class="flex-1">
        <label class="block text-xs uppercase tracking-[0.3em] text-steel-400">
          Search
          <input
            v-model.trim="searchQuery"
            type="text"
            placeholder="Search handle or name"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white placeholder:text-steel-500 focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            @input="queueFetch"
          />
        </label>
      </div>
      <div class="sm:w-48">
        <label class="block text-xs uppercase tracking-[0.3em] text-steel-400">
          Status
          <select
            v-model="statusFilter"
            class="mt-2 w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-4 py-3 text-sm text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
            @change="refreshUsers"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="banned">Banned</option>
          </select>
        </label>
      </div>
    </div>

    <p v-if="errorMessage" class="mt-6 rounded-2xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
      {{ errorMessage }}
    </p>
    <p v-if="successMessage" class="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">
      {{ successMessage }}
    </p>

    <div class="mt-6 rounded-3xl border border-white/10 bg-midnight-900/60">
      <div class="hidden grid-cols-[2fr_1fr_1fr_1fr_2fr] gap-4 border-b border-white/10 px-6 py-4 text-xs uppercase tracking-[0.3em] text-steel-400 sm:grid">
        <span>User</span>
        <span>{{ $t('adminPage.memberNumber') }}</span>
        <span>Role</span>
        <span>Status</span>
        <span>Actions</span>
      </div>
      <div v-if="loading" class="px-6 py-6 space-y-4">
        <div
          v-for="item in 5"
          :key="`member-skeleton-${item}`"
          class="grid gap-4 animate-pulse sm:grid-cols-[2fr_1fr_1fr_1fr_2fr]"
        >
          <div class="flex items-center gap-4">
            <div class="h-12 w-12 rounded-2xl bg-white/10"></div>
            <div class="flex-1 space-y-2">
              <div class="h-3 w-32 rounded bg-white/10"></div>
              <div class="h-2 w-20 rounded bg-white/10"></div>
            </div>
          </div>
          <div class="h-10 rounded-2xl bg-white/10"></div>
          <div class="h-10 rounded-2xl bg-white/10"></div>
          <div class="h-7 w-20 rounded-full bg-white/10"></div>
          <div class="flex gap-2">
            <div class="h-10 w-20 rounded-2xl bg-white/10"></div>
            <div class="h-10 w-24 rounded-2xl bg-white/10"></div>
            <div class="h-10 w-24 rounded-2xl bg-white/10"></div>
          </div>
        </div>
      </div>
      <div v-else-if="!users.length" class="px-6 py-6 text-sm text-steel-400">
        No members found.
      </div>
      <div v-else>
        <div
          v-for="user in users"
          :key="user.id"
          class="grid gap-4 border-b border-white/5 px-6 py-5 sm:grid-cols-[2fr_1fr_1fr_1fr_2fr]"
        >
          <router-link
            :to="`/u/${user.handle}`"
            class="flex items-center gap-4 hover:opacity-80 transition"
          >
            <div class="h-12 w-12 overflow-hidden rounded-2xl border border-white/10 bg-midnight-900/80">
              <img
                v-if="user.avatar"
                :src="user.avatar"
                alt=""
                class="h-full w-full object-cover"
                referrerpolicy="no-referrer"
                crossorigin="anonymous"
              />
              <div v-else class="flex h-full w-full items-center justify-center text-sm font-semibold text-steel-200">
                {{ user.initials }}
              </div>
            </div>
            <div>
              <p class="text-sm font-semibold text-white">
                {{ user.fullName || 'DRVN Member' }}
                <span v-if="user.id === currentUserId" class="ml-2 rounded-full bg-white/10 px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-steel-300">You</span>
              </p>
              <div class="mt-1 flex flex-wrap items-center gap-2">
                <p class="text-xs text-steel-400">@{{ user.handle }}</p>
                <span
                  v-if="user.verified"
                  class="rounded-full border border-sky-400/40 bg-sky-400/10 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-sky-300"
                >
                  Verified
                </span>
                <span class="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-steel-300 sm:hidden">
                  {{ $t('adminPage.memberNumber') }} #{{ user.memberNumber || '—' }}
                </span>
              </div>
            </div>
          </router-link>

          <div class="flex items-center">
            <span class="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-white">
              #{{ user.memberNumber || '—' }}
            </span>
          </div>

          <div>
            <select
              :value="user.role"
              class="w-full rounded-2xl border border-white/10 bg-midnight-900/60 px-3 py-2 text-xs uppercase tracking-[0.2em] text-white focus:border-neon-400/70 focus:outline-none focus:ring-2 focus:ring-neon-400/30"
              :disabled="user.id === currentUserId"
              @change="(event) => updateRole(user, event.target.value)"
            >
              <option v-for="role in roleOptions" :key="role" :value="role">{{ role }}</option>
            </select>
          </div>

          <div class="flex items-center gap-2">
            <span
              class="rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em]"
              :class="user.status === 'banned' ? 'bg-rose-500/20 text-rose-200' : 'bg-emerald-500/20 text-emerald-200'"
            >
              {{ user.status }}
            </span>
          </div>

          <div class="flex flex-wrap gap-2">
            <button
              type="button"
              class="rounded-2xl border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
              :disabled="user.id === currentUserId"
              @click="toggleBan(user)"
            >
              {{ user.status === 'banned' ? 'Unban' : 'Ban' }}
            </button>
            <button
              type="button"
              class="rounded-2xl border border-sky-400/35 bg-sky-400/10 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-sky-100 transition hover:bg-sky-400/20"
              @click="toggleVerified(user)"
            >
              {{ user.verified ? 'Unverify' : 'Verify' }}
            </button>
            <button
              type="button"
              class="rounded-2xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-100 transition hover:bg-amber-500/20"
              :disabled="user.id === currentUserId"
              @click="deleteUserData(user)"
            >
              Delete Data
            </button>
            <button
              type="button"
              class="rounded-2xl border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] text-rose-100 transition hover:bg-rose-500/20"
              :disabled="user.id === currentUserId"
              @click="deleteAuthUser(user)"
            >
              Delete Auth
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="mt-6 flex flex-wrap items-center justify-between gap-3">
      <p class="text-xs uppercase tracking-[0.3em] text-steel-500">
        Showing {{ users.length }} members
      </p>
      <button
        v-if="canLoadMore"
        type="button"
        class="rounded-2xl border border-white/10 bg-white/5 px-4 py-2 text-xs font-semibold uppercase tracking-[0.3em] text-white transition hover:bg-white/10"
        :disabled="loadingMore"
        @click="loadMore"
      >
        {{ loadingMore ? 'Loading...' : 'Load more' }}
      </button>
    </div>

  </section>
</template>

<script>
import { supabase, getAvatarUrl, getReadySession, runSupabaseQueryWithRetry } from '@/lib/supabase'

export default {
  name: 'AdminDashboard',
  data() {
    return {
      users: [],
      loading: false,
      loadingMore: false,
      canLoadMore: true,
      searchQuery: '',
      statusFilter: 'all',
      errorMessage: '',
      successMessage: '',
      page: 0,
      pageSize: 25,
      searchTimer: null,
      currentUserId: '',
      roleOptions: ['admin', 'moderator', 'member'],
      reports: [],
      reportsLoading: false,
      reportsError: '',
      siteReports: [],
      siteReportsLoading: false,
      siteReportsError: '',
      showDeleteReportConfirm: false,
      reportToDelete: null,
      stats: {
        total: 0,
        admins: 0,
        banned: 0,
      },
      pushSubscriberCount: 0,
      pushSubscriberLoading: false,
      pushSubscriberMembers: [],
      broadcastSending: false,
      broadcastForm: {
        title: 'DRVN update',
        body: 'Open DRVN to see the latest announcement.',
        url: '/notifications',
      },
    }
  },
  computed: {
    subscribedMembers() {
      return this.pushSubscriberMembers.filter((member) => member.pushEnabled)
    },
    unsubscribedMembers() {
      return this.pushSubscriberMembers.filter((member) => !member.pushEnabled)
    },
  },
  async mounted() {
    const session = await getReadySession()
    this.currentUserId = session?.user?.id || ''
    await this.refreshUsers()
    await this.fetchPushSubscriberCount()
    await this.fetchReports()
    await this.fetchSiteReports()
  },
  methods: {
    async fetchPushSubscriberCount() {
      this.pushSubscriberLoading = true
      const { data, error } = await supabase.functions.invoke('list-push-subscribers')

      this.pushSubscriberLoading = false

      if (error) {
        this.errorMessage = error.message || 'Unable to load push subscriber count.'
        return
      }

      this.pushSubscriberMembers = (data?.members || []).map((member) => {
        const avatar = getAvatarUrl({ avatarPath: member.avatarPath, avatarUrl: member.avatarUrl })
        const fullName = member.fullName || ''
        const initials = (fullName || member.handle || 'D')
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .map((chunk) => chunk[0].toUpperCase())
          .join('')

        return {
          ...member,
          avatar,
          initials,
        }
      })
      this.pushSubscriberCount = Number(data?.subscribedCount || 0)
    },
    formatAdminDate(value) {
      if (!value) return this.$t('adminPage.pushNeverSeen')
      return new Date(value).toLocaleString()
    },
    applyUserFilters(query) {
      if (this.statusFilter !== 'all') {
        query = query.eq('status', this.statusFilter)
      }

      const cleaned = this.searchQuery.replace(/^@/, '')
      if (cleaned) {
        query = query.or(`handle.ilike.%${cleaned}%,full_name.ilike.%${cleaned}%`)
      }

      return query
    },
    async refreshStats() {
      const [totalResult, adminResult, bannedResult] = await Promise.all([
        this.applyUserFilters(
          supabase.from('profiles').select('id', { count: 'exact', head: true })
        ),
        this.applyUserFilters(
          supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('role', 'admin')
        ),
        this.applyUserFilters(
          supabase.from('profiles').select('id', { count: 'exact', head: true }).eq('status', 'banned')
        ),
      ])

      this.stats.total = totalResult.count || 0
      this.stats.admins = adminResult.count || 0
      this.stats.banned = bannedResult.count || 0
    },
    queueFetch() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.refreshUsers()
      }, 250)
    },
    async refreshUsers() {
      this.page = 0
      this.canLoadMore = true
      await this.fetchUsers(true)
    },
    async loadMore() {
      if (!this.canLoadMore || this.loadingMore) return
      this.page += 1
      await this.fetchUsers(false)
    },
    async fetchUsers(reset) {
      this.errorMessage = ''
      this.successMessage = ''
      this.loading = reset
      this.loadingMore = !reset
      const from = this.page * this.pageSize
      const to = from + this.pageSize - 1

      const buildUsersQuery = () => {
        let query = supabase
          .from('profiles')
          .select('id, handle, full_name, avatar_url, avatar_path, role, verified, status, created_at, member_number', { count: 'exact' })
          .order('member_number', { ascending: false })
          .range(from, to)

        return this.applyUserFilters(query)
      }

      const { data, error, count } = await runSupabaseQueryWithRetry(buildUsersQuery)

      if (error) {
        this.errorMessage = error.message || 'Unable to load members.'
        this.loading = false
        this.loadingMore = false
        return
      }

      const normalized = (data || []).map((user) => {
        const avatar = getAvatarUrl({ avatarPath: user.avatar_path, avatarUrl: user.avatar_url })
        const fullName = user.full_name || ''
        const initials = (fullName || user.handle || 'D')
          .split(' ')
          .filter(Boolean)
          .slice(0, 2)
          .map((s) => s[0].toUpperCase())
          .join('')
        return {
          id: user.id,
          handle: user.handle || 'member',
          fullName,
          avatar,
          role: user.role || 'member',
          verified: Boolean(user.verified),
          status: user.status || 'active',
          memberNumber: Number(user.member_number) || null,
          initials,
        }
      })

      if (reset) {
        this.users = normalized
      } else {
        this.users = [...this.users, ...normalized]
      }

      this.canLoadMore = normalized.length === this.pageSize
      this.loading = false
      this.loadingMore = false

      this.stats.total = count || this.users.length
      await this.refreshStats()
    },
    async updateRole(user, newRole) {
      if (user.role === newRole) return
      this.errorMessage = ''
      this.successMessage = ''
      const { error } = await supabase.from('profiles').update({ role: newRole }).eq('id', user.id)
      if (error) {
        this.errorMessage = error.message || 'Unable to update role.'
        return
      }
      user.role = newRole
      this.successMessage = `Role updated for @${user.handle}.`
      await this.refreshStats()
    },
    async toggleVerified(user) {
      this.errorMessage = ''
      this.successMessage = ''
      const nextVerified = !user.verified
      const { error } = await supabase.from('profiles').update({ verified: nextVerified }).eq('id', user.id)
      if (error) {
        this.errorMessage = error.message || 'Unable to update verified status.'
        return
      }
      user.verified = nextVerified
      this.successMessage = nextVerified
        ? `@${user.handle} is now verified.`
        : `Verified badge removed for @${user.handle}.`
    },
    async toggleBan(user) {
      if (user.id === this.currentUserId) return
      const makeBanned = user.status !== 'banned'
      const confirmText = makeBanned
        ? `Ban @${user.handle}? They will be signed out on next session refresh.`
        : `Unban @${user.handle} and restore access?`
      if (!window.confirm(confirmText)) return

      this.errorMessage = ''
      this.successMessage = ''
      const payload = makeBanned
        ? { status: 'banned', banned_at: new Date().toISOString() }
        : { status: 'active', banned_at: null }

      const { error } = await supabase.from('profiles').update(payload).eq('id', user.id)
      if (error) {
        this.errorMessage = error.message || 'Unable to update status.'
        return
      }
      user.status = payload.status
      this.successMessage = makeBanned ? `@${user.handle} banned.` : `@${user.handle} unbanned.`
      await this.refreshStats()
    },
    async deleteUserData(user) {
      if (user.id === this.currentUserId) return
      if (!window.confirm(`Delete profile data for @${user.handle}? This cannot be undone.`)) return

      this.errorMessage = ''
      this.successMessage = ''
      const userId = user.id

      const { data: posts, error: postsError } = await supabase.from('posts').select('id').eq('user_id', userId)
      if (postsError) {
        this.errorMessage = postsError.message || 'Unable to load user posts.'
        return
      }
      const postIds = (posts || []).map((post) => post.id)
      if (postIds.length) {
        const { error: mediaError } = await supabase.from('post_media').delete().in('post_id', postIds)
        if (mediaError) {
          this.errorMessage = mediaError.message || 'Unable to delete post media.'
          return
        }
      }

      const deleteSteps = [
        supabase.from('post_comment_likes').delete().eq('user_id', userId),
        supabase.from('post_comments').delete().eq('user_id', userId),
        supabase.from('post_likes').delete().eq('user_id', userId),
        supabase.from('post_reactions').delete().eq('user_id', userId),
        supabase.from('notifications').delete().or(`recipient_id.eq.${userId},actor_id.eq.${userId}`),
        supabase.from('posts').delete().eq('user_id', userId),
        supabase.from('profiles').delete().eq('id', userId),
      ]

      for (const step of deleteSteps) {
        const { error } = await step
        if (error) {
          this.errorMessage = error.message || 'Unable to delete user data.'
          return
        }
      }

      this.users = this.users.filter((item) => item.id !== userId)
      this.successMessage = `Deleted profile data for @${user.handle}.`
      await this.refreshStats()
    },
    async deleteAuthUser(user) {
      if (user.id === this.currentUserId) return
      if (!window.confirm(`Delete auth user for @${user.handle}? This removes their login credentials.`)) return

      this.errorMessage = ''
      this.successMessage = ''
      const { error } = await supabase.functions.invoke('admin-delete-user', {
        body: { userId: user.id },
      })
      if (error) {
        this.errorMessage = error.message || 'Unable to delete auth user.'
        return
      }
      this.successMessage = `Auth user deleted for @${user.handle}.`
    },
    async sendBroadcastPush() {
      this.errorMessage = ''
      this.successMessage = ''
      this.broadcastSending = true

      try {
        const title = (this.broadcastForm.title || '').trim().slice(0, 80)
        const body = (this.broadcastForm.body || '').trim().slice(0, 240)
        const rawUrl = (this.broadcastForm.url || '').trim() || '/notifications'
        const url = rawUrl.startsWith('/') ? rawUrl : `/${rawUrl.replace(/^\/+/, '')}`

        if (!title || !body) {
          throw new Error('Broadcast title and message are required.')
        }

        const { data, error } = await supabase.functions.invoke('send-broadcast-push', {
          body: { title, body, url },
        })

        if (error) {
          throw error
        }

        this.broadcastForm.url = url
        this.successMessage = `Broadcast sent to ${Number(data?.sent || 0)} subscribed devices.`
        await this.fetchPushSubscriberCount()
      } catch (error) {
        this.errorMessage = error.message || 'Unable to send the broadcast push notification.'
      } finally {
        this.broadcastSending = false
      }
    },
    async fetchReports() {
      this.reportsError = ''
      this.reportsLoading = true

      const { data, error } = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('post_reports')
          .select('id, post_id, reporter_id, reason, status, created_at')
          .order('created_at', { ascending: false })
          .limit(100)
      ))

      if (error) {
        this.reportsError = error.message || 'Unable to load reports.'
        this.reportsLoading = false
        return
      }

      const reporterIds = (data || []).map((report) => report.reporter_id).filter(Boolean)
      let profilesById = new Map()

      if (reporterIds.length) {
        const { data: profiles } = await runSupabaseQueryWithRetry(() => (
          supabase
            .from('profiles')
            .select('id, handle, full_name, avatar_url, avatar_path')
            .in('id', reporterIds)
        ))

        profilesById = new Map((profiles || []).map((profile) => [profile.id, profile]))
      }

      this.reports = (data || []).map((report) => {
        const reporter = profilesById.get(report.reporter_id) || {}
        const reporterAvatar = getAvatarUrl({
          avatarPath: reporter.avatar_path,
          avatarUrl: reporter.avatar_url,
        })
        return {
          id: report.id,
          postId: report.post_id,
          reporterId: report.reporter_id,
          reporterHandle: reporter.handle || '',
          reporterName: reporter.full_name || '',
          reporterAvatar,
          reason: report.reason || '',
          status: report.status || 'pending',
          createdLabel: report.created_at ? new Date(report.created_at).toLocaleString() : '',
        }
      })

      this.reportsLoading = false
    },
    async fetchSiteReports() {
      this.siteReportsError = ''
      this.siteReportsLoading = true

      const { data, error } = await runSupabaseQueryWithRetry(() => (
        supabase
          .from('site_reports')
          .select('id, user_id, category, description, page_url, status, created_at, profiles (handle, full_name)')
          .order('created_at', { ascending: false })
          .limit(100)
      ))

      if (error) {
        this.siteReportsError = error.message || 'Unable to load site reports.'
        this.siteReportsLoading = false
        return
      }

      this.siteReports = (data || []).map((report) => ({
        id: report.id,
        userId: report.user_id,
        category: report.category || 'other',
        description: report.description || '',
        pageUrl: report.page_url || '',
        status: report.status || 'open',
        createdLabel: report.created_at ? new Date(report.created_at).toLocaleString() : '',
        reporterHandle: report.profiles?.handle || '',
        reporterName: report.profiles?.full_name || '',
      }))

      this.siteReportsLoading = false
    },
    async resolveSiteReport(report) {
      if (!report?.id) return
      const { error } = await supabase.from('site_reports').update({ status: 'resolved' }).eq('id', report.id)
      if (error) {
        this.siteReportsError = error.message || 'Unable to update site report.'
        return
      }
      report.status = 'resolved'
    },
    async deleteSiteReport(report) {
      if (!report?.id) return
      if (!window.confirm('Delete this site issue from the database?')) return

      this.siteReportsError = ''
      this.successMessage = ''
      const { error } = await supabase.from('site_reports').delete().eq('id', report.id)
      if (error) {
        this.siteReportsError = error.message || 'Unable to delete site report.'
        return
      }

      this.siteReports = this.siteReports.filter((entry) => entry.id !== report.id)
      this.successMessage = 'Site issue deleted from the database.'
    },
    async resolveReport(report) {
      if (!report?.id) return
      const { error } = await supabase.from('post_reports').update({ status: 'resolved' }).eq('id', report.id)
      if (error) {
        this.reportsError = error.message || 'Unable to update report.'
        return
      }
      report.status = 'resolved'
    },
    async deleteReportRecord(report) {
      if (!report?.id) return
      if (!window.confirm('Delete this moderation report from the database?')) return

      this.reportsError = ''
      this.successMessage = ''
      const { error } = await supabase.from('post_reports').delete().eq('id', report.id)
      if (error) {
        this.reportsError = error.message || 'Unable to delete report.'
        return
      }

      this.reports = this.reports.filter((entry) => entry.id !== report.id)
      this.successMessage = 'Moderation report deleted from the database.'
    },
    openDeleteReportConfirm(report) {
      if (!report?.postId) return
      this.reportToDelete = report
      this.showDeleteReportConfirm = true
    },
    closeDeleteReportConfirm() {
      this.showDeleteReportConfirm = false
      this.reportToDelete = null
    },
    async confirmDeleteReportedPost() {
      if (!this.reportToDelete?.postId) {
        this.closeDeleteReportConfirm()
        return
      }

      const postId = this.reportToDelete.postId
      const [{ error: postError }, { error: reportError }] = await Promise.all([
        supabase.from('posts').delete().eq('id', postId),
        supabase.from('post_reports').delete().eq('post_id', postId),
      ])

      if (postError || reportError) {
        this.reportsError = postError?.message || reportError?.message || 'Unable to delete post.'
        this.closeDeleteReportConfirm()
        return
      }

      this.reports = this.reports.filter((entry) => entry.postId !== postId)
      this.successMessage = 'Post and related reports deleted from the database.'
      this.closeDeleteReportConfirm()
    },
  },
}
</script>
